/**
 * Generic, typed worker RPC utilities.
 *
 * Provides a reusable singleton worker factory and a promise-based
 * request/response wrapper with automatic listener cleanup, request ID
 * tracking for concurrent calls, and transferable collection.
 */

// ---------------------------------------------------------------------------
// Singleton factory
// ---------------------------------------------------------------------------

/**
 * Returns a lazy-initialised singleton getter.  The worker is created on
 * first call and reused for all subsequent calls.
 *
 * If the worker fails to load (e.g. the script URL returns HTML instead of JS
 * after a dev-server restart or a stale deployment), the singleton is cleared
 * so the next call can retry rather than reusing a dead Worker indefinitely.
 */
export function createSingletonWorker(factory: () => Worker): () => Worker {
  let worker: Worker | null = null;
  return () => {
    if (!worker) {
      const w = factory();
      // One-shot load-error listener: clear the singleton so subsequent calls
      // create a fresh worker instead of posting into a dead one.
      w.addEventListener(
        "error",
        () => {
          if (worker === w) worker = null;
        },
        { once: true },
      );
      worker = w;
    }
    return worker;
  };
}

// ---------------------------------------------------------------------------
// RPC call
// ---------------------------------------------------------------------------

let _nextId = 1;

interface RPCEnvelope<T> {
  _rpcId: number;
  payload: T;
}

interface RPCResponse<T> {
  _rpcId: number;
  ok: boolean;
  result?: T;
  error?: string;
}

export interface CallWorkerOptions {
  /**
   * If set, a warning toast is shown when the worker hasn't responded
   * within this many milliseconds. The call itself is NOT aborted —
   * the toast is purely informational. Pass `0` or omit to disable.
   */
  stallWarningMs?: number;
  /** Custom stall-warning message (falls back to a generic default). */
  stallMessage?: string;
  /**
   * If set, only one stall warning with this key is shown at a time.
   * Subsequent warnings with the same key are suppressed while one is visible.
   */
  stallDedupKey?: string;
}

/**
 * Send a request to a worker and return a promise for the response.
 *
 * Each call gets a unique `_rpcId` so multiple in-flight requests can be
 * multiplexed on the same worker.  The worker must reply with a message
 * that includes the same `_rpcId`.
 *
 * If `transferables` is omitted, `collectTransferables(message)` is used
 * to automatically detect typed-array buffers.
 */
export function callWorker<TIn, TOut>(
  worker: Worker,
  message: TIn,
  transferables?: Transferable[],
  options?: CallWorkerOptions,
): Promise<TOut> {
  const id = _nextId++;
  const envelope: RPCEnvelope<TIn> = { _rpcId: id, payload: message };
  const xfer = transferables ?? collectTransferables(message);

  return new Promise<TOut>((resolve, reject) => {
    let stallTimer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);
      if (stallTimer) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    };

    const onMessage = (e: MessageEvent<RPCResponse<TOut>>) => {
      if (e.data._rpcId !== id) return; // not our response
      cleanup();
      if (e.data.ok) {
        resolve(e.data.result as TOut);
      } else {
        reject(new Error(e.data.error ?? "Worker error"));
      }
    };

    const onError = (e: ErrorEvent) => {
      cleanup();
      reject(new Error(e.message ?? "Worker error"));
    };

    worker.addEventListener("message", onMessage);
    worker.addEventListener("error", onError);

    // Optional stall detection — show a toast if the worker takes too long
    if (options?.stallWarningMs && options.stallWarningMs > 0) {
      stallTimer = setTimeout(() => {
        // Lazy-import to avoid circular dependency (workers don't have DOM)
        import("../ui/toastBus.ts")
          .then(({ showToast }) => {
            showToast(
              options.stallMessage ??
                "A background task is taking longer than expected.",
              {
                severity: "warning",
                duration: 6000,
                dedupKey: options.stallDedupKey,
              },
            );
          })
          .catch(() => {
            /* worker context — no DOM, ignore */
          });
      }, options.stallWarningMs);
    }

    worker.postMessage(envelope, xfer as any);
  });
}

// ---------------------------------------------------------------------------
// Worker-side helper
// ---------------------------------------------------------------------------

/**
 * Wrap a handler function so it speaks the RPC envelope protocol.
 * Use this inside the worker's `self.onmessage` to automatically
 * unwrap requests and wrap responses with the correct `_rpcId`.
 *
 * The handler receives the unwrapped payload and returns the result
 * (or throws).  Transferables for the response can be provided via
 * the optional second return value.
 */
type HandlerResult<TOut> = TOut | { result: TOut; transfer: Transferable[] };

export function handleRPC<TIn, TOut>(
  handler: (payload: TIn) => HandlerResult<TOut> | Promise<HandlerResult<TOut>>,
): (e: MessageEvent<RPCEnvelope<TIn>>) => void {
  return (e: MessageEvent<RPCEnvelope<TIn>>) => {
    const { _rpcId, payload } = e.data;
    Promise.resolve()
      .then(() => handler(payload))
      .then((out) => {
        if (out && typeof out === "object" && "transfer" in (out as any)) {
          const { result, transfer } = out as {
            result: TOut;
            transfer: Transferable[];
          };
          self.postMessage({ _rpcId, ok: true, result }, transfer as any);
        } else {
          self.postMessage({ _rpcId, ok: true, result: out });
        }
      })
      .catch((err) => {
        self.postMessage({
          _rpcId,
          ok: false,
          error: err instanceof Error ? err.message : String(err),
        });
      });
  };
}

// ---------------------------------------------------------------------------
// Transfer utilities
// ---------------------------------------------------------------------------

/**
 * Walk an object's properties and collect all unique non-empty `ArrayBuffer`
 * instances from typed arrays. Handles nested objects and arrays (one level
 * deep for arrays of typed arrays, e.g. morphArrays).
 *
 * Zero-length buffers are intentionally skipped. Several systems use shared
 * empty typed-array sentinels (for example `EMPTY_RENDER_TRIANGLE_INDICES`);
 * transferring those through `postMessage` detaches the singleton in the
 * sender realm, and the next worker response that reuses it fails structured
 * clone with a `DataCloneError`.
 */
export function collectTransferables(msg: unknown): Transferable[] {
  const buffers = new Set<ArrayBuffer>();

  function walk(val: unknown): void {
    if (!val || typeof val !== "object") return;
    if (ArrayBuffer.isView(val)) {
      const buf = (val as ArrayBufferView).buffer;
      if (buf instanceof ArrayBuffer && buf.byteLength > 0) buffers.add(buf);
      return;
    }
    if (val instanceof ArrayBuffer) {
      if (val.byteLength > 0) buffers.add(val);
      return;
    }
    if (Array.isArray(val)) {
      for (const item of val) walk(item);
      return;
    }
    for (const v of Object.values(val as Record<string, unknown>)) {
      walk(v);
    }
  }

  walk(msg);
  return [...buffers];
}
